import { Box, Text } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker, useNavigate, useParams } from 'react-router'
import { switchboardApi } from '~/apis/switchboardApi'
import { useUserState } from '~/atoms/UserState'
import { useColorMode } from '~/components/ui/color-mode'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { isUserSuperuser } from '~/utils/auth'
import { getApiEnvironment } from '~/utils/common'

import { LoadingState } from '@mangovoice/mango-ui'
import {
  DeleteSwitchboardConfirmationDialog,
  getSwitchboardDisplayName,
  SwitchboardEditPage,
  useGetSwitchboard,
  useGetSwitchboardExtensions,
  useSwitchboardDataAtoms,
} from '@mangovoice/switchboard-ui'

import {
  useGetAiAgents,
  useGetMessagingGroups,
  useGetPhoneNumbers,
  useGetSchedules,
  useGetSoundClips,
  useSwitchboardDelete,
} from './hooks'

const EXTENSION_REQUEST_CONFIG = { timeout: 60_000 }

/**
 * Switchboard Edit View for web-app
 * Displays the canvas/call flow editor for a specific switchboard
 */
export const SwitchboardEditView = () => {
  const { t } = useTranslation()
  const { extensionDialPlan } = useParams<{ extensionDialPlan: string }>()
  const navigate = useNavigate()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })
  const { colorMode } = useColorMode()
  const checkRole = useCheckRole()
  const canDelete = checkRole('senior')

  const extensionDialPlanNumber = extensionDialPlan
    ? parseInt(extensionDialPlan, 10)
    : 0

  // Fetch switchboard data
  const { data: switchboardData, isLoading: isLoadingSwitchboard } =
    useGetSwitchboard({
      api: switchboardApi,
      extensionDialPlan: extensionDialPlanNumber,
    })

  // Convert pbxId to null for hooks that expect number | null
  const pbxIdOrNull = pbxId || null

  // Fetch all call flow data using custom hooks
  const {
    extensionMap,
    extensionsList,
    isLoading: isLoadingExtensions,
  } = useGetSwitchboardExtensions({
    api: switchboardApi,
    pbxId: pbxIdOrNull,
    requestConfig: EXTENSION_REQUEST_CONFIG,
  })

  const {
    messagingGroups,
    isLoading: isLoadingMessagingGroups,
    isLoaded: messagingGroupsLoaded,
  } = useGetMessagingGroups(pbxIdOrNull)

  const {
    soundClips,
    isLoading: isLoadingSoundClips,
    refetch: refetchSoundClips,
  } = useGetSoundClips(
    pbxIdOrNull,
    switchboardData as { json_data?: string } | undefined,
  )

  const { schedules, isLoading: isLoadingSchedules } =
    useGetSchedules(pbxIdOrNull)

  const {
    phoneNumbers,
    smsEligiblePhoneNumbers,
    isLoading: isLoadingPhoneNumbers,
  } = useGetPhoneNumbers(pbxIdOrNull)

  const { aiAgents, isLoading: isLoadingAiAgents } = useGetAiAgents(pbxIdOrNull)

  // Delete functionality
  const {
    isDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  } = useSwitchboardDelete(extensionDialPlanNumber, pbxIdOrNull, {
    canDelete,
  })

  // Track unsaved changes for navigation blocking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const blocker = useBlocker(hasUnsavedChanges)

  // Get switchboard extension for display name
  const switchboardExtension = useMemo(() => {
    return extensionMap.get(extensionDialPlanNumber)
  }, [extensionMap, extensionDialPlanNumber])

  const switchboardDisplayName = useMemo(() => {
    return getSwitchboardDisplayName(switchboardExtension)
  }, [switchboardExtension])

  const handleRefreshReferenceData = useCallback(async () => {
    await refetchSoundClips()
  }, [refetchSoundClips])

  // Set Jotai atoms for reference data
  useSwitchboardDataAtoms({
    extensionsList,
    extensionMap,
    messagingGroups,
    messagingGroupsLoaded,
    soundClips,
    schedules,
    phoneNumbers,
    smsEligiblePhoneNumbers,
    aiAgents,
    currentSwitchboardId: extensionDialPlanNumber,
    apiEnvironment: getApiEnvironment(),
  })

  // Early returns after all hooks
  if (!extensionDialPlanNumber) {
    return (
      <Box textAlign="center" py={8}>
        <Text>
          {t('switchboardEdit.invalidDialPlan', 'Invalid extension dial plan')}
        </Text>
      </Box>
    )
  }

  if (!pbxId) {
    return (
      <Box textAlign="center" py={8}>
        <Text>{t('switchboardEdit.invalidPbxId', 'Invalid PBX ID')}</Text>
      </Box>
    )
  }

  if (
    isLoadingSwitchboard ||
    isLoadingExtensions ||
    isLoadingMessagingGroups ||
    isLoadingSoundClips ||
    isLoadingSchedules ||
    isLoadingPhoneNumbers ||
    isLoadingAiAgents
  ) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        w="100%"
      >
        <LoadingState
          title={t('switchboardEdit.loading', 'Loading switchboard...')}
        />
      </Box>
    )
  }

  return (
    <>
      <SwitchboardEditPage
        key={extensionDialPlanNumber}
        api={switchboardApi}
        extensionDialPlan={extensionDialPlanNumber}
        navigate={navigate}
        listRoute={ApplicationRoutes.SWITCHBOARDS}
        pbxId={pbxId}
        toast={toaster}
        onDelete={canDelete ? handleDeleteClick : undefined}
        isStaff={!!(userRoles?.is_staff || userRoles?.is_superuser)}
        isSuperuser={isUserSuperuser(userRoles)}
        blocker={blocker}
        onUnsavedChangesChange={setHasUnsavedChanges}
        colorMode={colorMode}
        t={t}
        onRefreshReferenceData={handleRefreshReferenceData}
      />
      <DeleteSwitchboardConfirmationDialog
        isOpen={isDeleteDialogOpen}
        switchboardName={switchboardDisplayName || undefined}
        extensionDialPlan={extensionDialPlanNumber}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        t={t}
      />
    </>
  )
}
